import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';
import { useAuth } from '../components/auth/AuthContext';
import { getVisitorId } from '../services/fingerprint';

interface SearchLimits {
  remainingSearches: number;
  isLimited: boolean;
  totalSearches: number;
  isLoading: boolean;
}

const DEFAULT_FREE_SEARCHES = 5;

const initialLimits: SearchLimits = {
  remainingSearches: 0,
  isLimited: false,
  totalSearches: 0,
  isLoading: true
};

export const useSearchLimits = () => {
  const [limits, setLimits] = useState<SearchLimits>(initialLimits);
  const { user } = useAuth();

  // Only initialize visitor ID for anonymous users
  useEffect(() => {
    if (!user?.id) {
      getVisitorId().catch(error => {
        console.error('Failed to initialize visitor ID:', error);
      });
    }
  }, [user?.id]);

  const fetchLimits = async () => {
    try {
      if (!user?.id) {
        // For anonymous users, check visitor-based limits
        const sessionId = sessionStorage.getItem('footyAI_sessionId');
        const visitorId = await getVisitorId();

        if (!sessionId || !visitorId) {
          setLimits({
            ...initialLimits,
            isLoading: false,
            remainingSearches: DEFAULT_FREE_SEARCHES // Provide default searches even if session info is missing
          });
          return;
        }

        // Get credits from search_limits table
        const { data: searchLimits, error } = await supabase
          .from('search_limits')
          .select('credits')
          .eq('visitor_id', visitorId)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (error) throw error;

        if (searchLimits) {
          // User has a visitor record
          setLimits({
            remainingSearches: searchLimits.credits,
            isLimited: searchLimits.credits <= 0,
            totalSearches: DEFAULT_FREE_SEARCHES - searchLimits.credits,
            isLoading: false
          });
        } else {
          // New visitor, show default free searches
          setLimits({
            remainingSearches: DEFAULT_FREE_SEARCHES,
            isLimited: false,
            totalSearches: 0,
            isLoading: false
          });
        }
      } else {
        // For authenticated users, check credits
        const { data: credits, error } = await supabase
          .from('user_search_credits')
          .select('credits')
          .eq('user_id', user.id)
          .maybeSingle();

        if (error) {
          console.error('Error fetching user credits:', error);
          setLimits({
            ...initialLimits,
            isLoading: false
          });
          return;
        }

        // Update with user credits or fallback to initial state
        setLimits({
          remainingSearches: credits?.credits ?? 0,
          isLimited: (credits?.credits ?? 0) <= 0,
          totalSearches: 0,
          isLoading: false
        });
      }
    } catch (error) {
      console.error('Error fetching search limits:', error);
      setLimits({
        ...initialLimits,
        isLoading: false
      });
    }
  };

  // Fetch initial limits when component mounts and when auth state changes
  useEffect(() => {
    let isMounted = true;

    const loadLimits = async () => {
      if (!isMounted) return;
      
      setLimits(prev => ({ ...prev, isLoading: true }));
      
      // Add a small delay to ensure auth state is stable
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Only proceed if still mounted and auth state hasn't changed
      if (isMounted) {
        await fetchLimits();
      }
    };

    loadLimits();

    return () => {
      isMounted = false;
    };
  }, [user?.id]); // Re-fetch when user ID changes

  return {
    remainingSearches: limits.remainingSearches,
    isLimited: limits.isLimited,
    totalSearches: limits.totalSearches,
    isLoading: limits.isLoading,
    refetchLimits: fetchLimits // Export refetch function to update limits after searches
  };
};